import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import SimpleTooltip from '../SimpleTooltip';
import { Calendar, Clock, Shield, Video, Check, X, Play, CheckCircle2, Phone, GraduationCap, AlertTriangle } from 'lucide-react';

const STALE_GRACE_MINUTES = 30;

/**
 * An *active* session is "auto-stale" when its expected end time
 * (scheduled_date + scheduled_time + duration + STALE_GRACE_MINUTES)
 * is in the past — usually means the admin forgot to click Complete.
 * Surfacing this catches forgotten sessions before they keep re-firing
 * the user's "Your session is live!" banner.
 */
const isAutoStale = (session) => {
  if (session.status !== 'active') return false;
  if (!session.scheduled_date || !session.scheduled_time) return false;
  const duration = session.duration_minutes || (session.session_type === 'quick_setup' ? 15 : 60);
  const scheduledStart = new Date(`${session.scheduled_date}T${session.scheduled_time}:00Z`);
  if (Number.isNaN(scheduledStart.getTime())) return false;
  const expectedEnd = scheduledStart.getTime() + (duration + STALE_GRACE_MINUTES) * 60_000;
  return Date.now() > expectedEnd;
};

const minutesPastDue = (session) => {
  const duration = session.duration_minutes || (session.session_type === 'quick_setup' ? 15 : 60);
  const scheduledStart = new Date(`${session.scheduled_date}T${session.scheduled_time}:00Z`);
  const expectedEnd = scheduledStart.getTime() + (duration + STALE_GRACE_MINUTES) * 60_000;
  return Math.round((Date.now() - expectedEnd) / 60_000);
};

const STATUS_CONFIG = {
  pending_review: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700', icon: Clock },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: Check },
  scheduled: { label: 'Scheduled', color: 'bg-emerald-100 text-emerald-700', icon: Calendar },
  active: { label: 'In Progress', color: 'bg-green-100 text-green-800', icon: Play },
  completed: { label: 'Completed', color: 'bg-slate-100 text-slate-600', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-600', icon: X },
  rejected: { label: 'Declined', color: 'bg-red-100 text-red-600', icon: X },
};

const SessionActions = ({ session, onAction, onView }) => {
  if (session.status === 'pending_review' && session.session_type === 'deep_onboarding') {
    return (
      <>
        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white"
          onClick={() => onAction(session.session_id, 'approve')} data-testid={`approve-${session.session_id}`}>
          <Check className="w-3 h-3 mr-1" /> Approve
        </Button>
        <Button size="sm" variant="destructive"
          onClick={() => { const r = window.prompt('Reason:'); if (r !== null) onAction(session.session_id, 'reject', { reason: r }); }}
          data-testid={`reject-${session.session_id}`}>
          <X className="w-3 h-3 mr-1" /> Decline
        </Button>
      </>
    );
  }
  if (session.status === 'scheduled') {
    return (
      <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"
        onClick={() => onAction(session.session_id, 'start')} data-testid={`start-${session.session_id}`}>
        <Play className="w-3 h-3 mr-1" /> Start
      </Button>
    );
  }
  if (session.status === 'active') {
    return (
      <>
        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"
          onClick={() => onView(session)} data-testid={`view-${session.session_id}`}>
          <Video className="w-3 h-3 mr-1" /> View
        </Button>
        <Button size="sm" variant="outline"
          onClick={() => { const n = window.prompt('Notes:'); onAction(session.session_id, 'complete', { notes: n || '' }); }}
          data-testid={`complete-${session.session_id}`} className="text-white border-slate-500 hover:bg-slate-600">
          <CheckCircle2 className="w-3 h-3 mr-1" /> End
        </Button>
      </>
    );
  }
  return null;
};

const QualificationInfo = ({ qualification }) => (
  <div className="mt-2 bg-gray-700/50 rounded-lg p-3 space-y-2">
    <div>
      <span className="text-xs text-gray-400">Help topics:</span>
      <div className="flex flex-wrap gap-1 mt-0.5">
        {qualification.help_topics?.map(t => <Badge key={t} variant="outline" className="text-xs border-gray-500 text-gray-300">{t}</Badge>)}
      </div>
    </div>
    <div>
      <span className="text-xs text-gray-400">Asset types:</span>
      <div className="flex flex-wrap gap-1 mt-0.5">
        {qualification.asset_types?.map(a => <Badge key={a} variant="outline" className="text-xs border-gray-500 text-gray-300">{a}</Badge>)}
      </div>
    </div>
    {qualification.description && <p className="text-xs text-gray-300 italic">"{qualification.description}"</p>}
    <div className="text-xs text-gray-400">
      Investments: <span className="text-white font-medium">{qualification.investment_count || 0}</span>
    </div>
  </div>
);

const OnboardingSessionCard = ({ session, onAction, onView }) => {
  const cfg = STATUS_CONFIG[session.status] || STATUS_CONFIG.pending_review;
  const Icon = cfg.icon;
  const isQuick = session.session_type === 'quick_setup';
  const TypeIcon = isQuick ? Phone : GraduationCap;

  return (
    <Card key={session.session_id} className="border border-gray-600 bg-gray-800/50" data-testid={`admin-session-${session.session_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="space-y-1.5 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={isQuick ? 'bg-blue-900/50 text-blue-300' : 'bg-emerald-900/50 text-emerald-300'}>
                <TypeIcon className="w-3 h-3 mr-1" />
                {isQuick ? 'Quick Setup' : 'Deep Onboarding'}
              </Badge>
              <Badge className={cfg.color}><Icon className="w-3 h-3 mr-1" />{cfg.label}</Badge>
              {isAutoStale(session) && (
                <SimpleTooltip content={`Active for ${minutesPastDue(session)} min past expected end. Likely forgotten — please End the session.`}>
                  <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid={`auto-stale-${session.session_id}`}>
                    <AlertTriangle className="w-3 h-3 mr-1" /> Auto-stale
                  </Badge>
                </SimpleTooltip>
              )}
              {session.masked_mode && <Badge variant="outline" className="text-xs border-gray-500 text-gray-300"><Shield className="w-3 h-3 mr-1" /> Masked</Badge>}
            </div>

            <div className="flex items-center gap-4 text-sm">
              <span className="font-medium text-white">{session.user_name || session.user_email}</span>
              <span className="text-gray-400">{session.user_email}</span>
            </div>

            <div className="flex items-center gap-4 text-xs text-gray-400">
              {session.scheduled_date ? (
                <>
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {session.scheduled_date}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {session.scheduled_time} ({session.duration_minutes || (isQuick ? 15 : 60)} min)</span>
                </>
              ) : (
                <span>Not yet scheduled</span>
              )}
            </div>

            {session.qualification && <QualificationInfo qualification={session.qualification} />}
            {session.user_notes && !session.qualification && (
              <p className="text-xs text-gray-400 italic">"{session.user_notes}"</p>
            )}
          </div>

          <div className="flex items-center gap-2 ml-4">
            <SessionActions session={session} onAction={onAction} onView={onView} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export { OnboardingSessionCard, STATUS_CONFIG };
